import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const StyledThemeToggle = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 52px;
  height: 52px;
  min-width: 52px; /* Ensure consistent touch target */
  min-height: 52px;
  border-radius: 50%;
  border: 2px solid var(--green-tint);
  background: var(--navy);
  color: var(--lightest-slate);
  cursor: pointer;
  z-index: 1000;
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);

  /* Touch-friendly hover effects */
  @media (hover: hover) {
    &:hover {
      transform: translateY(-1px) scale(1.03);
      background: var(--light-navy);
      border-color: var(--green);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2), 0 3px 8px rgba(0, 0, 0, 0.12);
    }
  }

  /* Touch device active state */
  &:active {
    transform: translateY(0) scale(0.98);
    transition: all 0.1s ease-out;
    background: var(--green);
    color: var(--navy);
  }

  &:focus {
    outline: none;
    border-color: var(--green);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 3px var(--green-tint);
  }

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 56px; /* Slightly larger for better touch target */
    height: 56px;
    min-width: 56px;
    min-height: 56px;
    font-size: 20px;
  }

  @media (max-width: 480px) {
    bottom: 16px;
    right: 16px;
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
  }

  .icon {
    transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      transition: all 0.2s ease;
      color: currentColor;
      opacity: 0.9;
    }
  }

  &:hover .icon {
    transform: rotate(90deg) scale(1.05);

    svg {
      opacity: 1;
    }
  }
`;

const ThemeToggle = () => {
  const [isDark, setIsDark] = useState(true); // Default to dark theme

  // Apply theme to CSS custom properties
  const applyTheme = themeName => {
    // Import themes dynamically
    import('../styles/themes').then(({ getTheme }) => {
      const selectedTheme = getTheme(themeName);
      const root = document.documentElement;

      // Helper function to convert hex to rgba
      const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      // Helper function to determine if a color is dark
      const isDarkTheme = color => {
        const hex = color.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance < 0.5;
      };

      // Map theme properties to CSS variables
      const cssVariableMap = {
        '--navy': selectedTheme.background,
        '--light-navy': selectedTheme.backgroundSecondary,
        '--lightest-navy': selectedTheme.backgroundTertiary,
        '--dark-navy': selectedTheme.backgroundAccent,
        '--lightest-slate': selectedTheme.text,
        '--slate': selectedTheme.textSecondary,
        '--light-slate': selectedTheme.textTertiary,
        '--dark-slate': selectedTheme.textMuted,
        '--white-new': selectedTheme.accent,
        '--white-new-tint': selectedTheme.accentTint,
        '--pink': selectedTheme.accentSecondary,
        '--blue': selectedTheme.accentTertiary,
        '--white': selectedTheme.white,
        '--pure-white': selectedTheme.pureWhite,
        '--black': selectedTheme.black,
        '--navy-shadow': selectedTheme.shadow,
        '--green': selectedTheme.accent,
        '--green-tint': selectedTheme.accentTint,
        '--nav-bg': hexToRgba(selectedTheme.background, 0.85),
        '--featured-overlay': selectedTheme.primary,
        '--logo-fill': isDarkTheme(selectedTheme.background) ? '#0a192f' : selectedTheme.text,
      };

      // Apply all CSS variables to root
      Object.entries(cssVariableMap).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });

      // Apply Prism syntax highlighting colors based on theme
      const isDarkBg = isDarkTheme(selectedTheme.background);
      const prismColors = isDarkBg
        ? {
          // Dark theme Prism colors
          '--prism-bg': '#112340',
          '--prism-text': '#a2aabc',
          '--prism-comment': '#8695b799',
          '--prism-line-highlight': '#1d2d50',
          '--prism-blue': '#5ccfe6',
          '--prism-purple': '#c3a6ff',
          '--prism-green': '#bae67e',
          '--prism-yellow': '#ffd580',
          '--prism-orange': '#ffae57',
          '--prism-red': '#ef6b73',
        }
        : {
          // Light theme Prism colors
          '--prism-bg': '#f8f9fa',
          '--prism-text': '#656d76',
          '--prism-comment': '#6e7781',
          '--prism-line-highlight': '#e9ecef',
          '--prism-blue': '#0969da',
          '--prism-purple': '#8250df',
          '--prism-green': '#1a7f37',
          '--prism-yellow': '#9a6700',
          '--prism-orange': '#bc4c00',
          '--prism-red': '#cf222e',
        };

      // Apply Prism colors
      Object.entries(prismColors).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });

      // Add fast, smooth transitions for theme changes
      const style = document.createElement('style');
      style.id = 'theme-transitions';
      style.textContent = `
        * {
          transition: background-color 0.2s ease, 
                     color 0.2s ease, 
                     border-color 0.2s ease, 
                     fill 0.2s ease !important;
        }
        
        /* Specific optimizations for common elements */
        body, main, header, nav, section, div, 
        button, a, p, h1, h2, h3, h4, h5, h6 {
          transition: background-color 0.15s ease, 
                     color 0.15s ease, 
                     border-color 0.15s ease !important;
        }
      `;

      // Remove existing style if present
      const existingStyle = document.getElementById('theme-transitions');
      if (existingStyle) {
        existingStyle.remove();
      }

      // Add new style
      document.head.appendChild(style);
    });
  };

  // Handle theme toggle
  const handleToggle = () => {
    const newIsDark = !isDark;

    // Immediate state update
    setIsDark(newIsDark);

    // Apply the appropriate theme instantly
    if (newIsDark) {
      applyTheme('original');
    } else {
      applyTheme('originalLight');
    }

    // Store preference in localStorage
    localStorage.setItem('darkMode', newIsDark.toString());
  };

  // Load theme preference from localStorage on mount
  useEffect(() => {
    // Small delay to ensure this runs after layout.js
    setTimeout(() => {
      const savedTheme = localStorage.getItem('darkMode');
      if (savedTheme !== null) {
        const isDarkSaved = savedTheme === 'true';
        setIsDark(isDarkSaved);
        applyTheme(isDarkSaved ? 'original' : 'originalLight');
      } else {
        // No saved preference, set the default (dark) and save it
        setIsDark(true);
        applyTheme('original');
        localStorage.setItem('darkMode', 'true');
      }
    }, 5);
  }, []);

  return (
    <StyledThemeToggle
      onClick={handleToggle}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme`}
      title={`Currently ${isDark ? 'dark' : 'light'} theme. Click to switch to ${
        isDark ? 'light' : 'dark'
      } theme`}
      role="button"
      tabIndex={0}>
      <div className="icon" aria-hidden="true">
        {isDark ? (
          // Sun icon for switching to light theme
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2">
            <circle cx="12" cy="12" r="5" />
            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
          </svg>
        ) : (
          // Moon icon for switching to dark theme
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        )}
      </div>
    </StyledThemeToggle>
  );
};

export default ThemeToggle;
